import { takeEvery, call, all, fork, put } from "@redux-saga/core/effects";
import {
  insertIntegrations,
  getIntegrations,
  deleteIntegration,
  updateIntegration,
  getOwnedSharedIntegrations,
  getSharedIntegrationBySharedUserId,
  getSharedIntegrations
} from "api/Integrations";
import {
  addIntegration,
  removeDeletedIntegration,
  updateUpdatedIntegration,
  setOwnedSharedIntegrations,
  setSharedIntegrationsShared,
  setSharedIntegrations
} from "redux/actions/portal/integrationActionCreator";
import { closeNewIntegrationDialog } from "redux/actions/portal/integrationActionCreator";
import { setIntegrations } from "redux/actions/portal/integrationActionCreator";
import { setIntegrationMutatingFlag } from "redux/actions/portal/integrationActionCreator";
import {
  CREATE_NEW_INTEGRATION,
  FETCH_INTEGRATIONS,
  DELETE_INTEGRATION,
  UPDATE_INTEGRATION,
  FETCH_OWNED_SHARED_INTEGRATIONS,
  FETCH_SHARED_INTEGRATIONS_SHARED,
  FETCH_SHARED_INTEGRATIONS
} from "redux/constants/portal";
import { toast } from "react-toastify";

function* handleCreateNewIntegration({ payload }) {
  try {
    const {
      tenant_id,
      create_user_id,
      modified_user_id,
      description,
      flows,
      version,
      integrationName,
      logo
    } = payload;

    const apiPayload = {
      tenant_id,
      integration_name: integrationName,
      flow_ids: flows ?? [],
      description,
      create_user_id,
      modified_user_id,
      current_version: version,
      status: "active",
      logo
    };

    const result = yield call(insertIntegrations, apiPayload);
    
    yield put(addIntegration(Object.assign(result, apiPayload)));
    yield put(setIntegrationMutatingFlag(false));
    yield put(closeNewIntegrationDialog());
    toast.success(`Integration created successfully`);
  } catch (error) {
    console.error(error);
  }
}

function* handleDeleteIntegration({ payload }) {
  try {
    const apiPayload = {
      tenant_id: payload.tenant_id,
      integration_id: payload.integration_id
    };
    const response = yield call(deleteIntegration, apiPayload);

    if (!response?.isSuccessful) {
      yield put(setIntegrationMutatingFlag(false));
      toast.error(response?.message || "Failed to delete the integration");
      return;
    }

    yield put(removeDeletedIntegration({ id: payload.integration_id }));
    yield put(setIntegrationMutatingFlag(false));
    toast.success(`Integration deleted successfully`);
    payload.onSuccess?.();
  } catch (error) {
    yield put(setIntegrationMutatingFlag(false));
    toast.error("Failed to delete the integration");
  }
}

function* handleUpdateIntegration({ payload }) {
  try {
    const {
      id,
      tenant_id,
      create_user_id,
      modified_user_id,
      description,
      flows,
      version,
      integration_name,
      documentation,
      logo
    } = payload;
    const apiPayload = {
      tenant_id,
      integration_id: id,
      integration_name,
      flow_ids: flows ?? [],
      description,
      create_user_id,
      modified_user_id,
      current_version: version,
      status: "active",
      documentation,
      logo
    };

    yield call(updateIntegration, apiPayload);
    const { integration_id, ...rest } = apiPayload;
    yield put(
      updateUpdatedIntegration({
        ...rest,
        id,
        updated_date_time: new Date().toISOString()
      })
    );
    yield put(setIntegrationMutatingFlag(false));
    toast.success(`Integration updated successfully`);
  } catch (error) {
    console.error(error);
  }
}

function* handleFetchIntegrations({ payload }) {
  try {
    const { tenant_id } = payload;

    const result = yield call(getIntegrations, tenant_id);

    yield put(setIntegrations(Array.isArray(result) ? result : []));
  } catch (error) {
    console.error(error);
  }
}

function* handleFetchOwnedSharedIntegrations({ payload }) {
  try {
    const { auth0_id } = payload;
    const result = yield call(getOwnedSharedIntegrations, auth0_id);
    yield put(setOwnedSharedIntegrations(result));
  } catch (error) {
    console.error(error);
  }
}

function* handleFetchSharedIntegrationsShared({ payload }) {
  try {
    const { auth0_id } = payload;
    const result = yield call(getSharedIntegrationBySharedUserId, auth0_id);

    yield put(setSharedIntegrationsShared(result));
  } catch (error) {
    console.error(error);
  }
}

function* handleFetchSharedIntegrations({ payload }) {
  try {
    const { tenant_id } = payload;
    const result = yield call(getSharedIntegrations, tenant_id);
    yield put(setSharedIntegrations(result));
  } catch (error) {
    console.error(error);
  }
}

function* watchFetchIntegrations() {
  yield takeEvery(FETCH_INTEGRATIONS, handleFetchIntegrations);
}

function* watchCreateNewIntegration() {
  yield takeEvery(CREATE_NEW_INTEGRATION, handleCreateNewIntegration);
}

function* watchDeleteIntegration() {
  yield takeEvery(DELETE_INTEGRATION, handleDeleteIntegration);
}

function* watchUpdateIntegration() {
  yield takeEvery(UPDATE_INTEGRATION, handleUpdateIntegration);
}

function* watchFetchOwnedSharedIntegrations() {
  yield takeEvery(FETCH_OWNED_SHARED_INTEGRATIONS, handleFetchOwnedSharedIntegrations);
}

function* watchFetchSharedIntegrationsShared() {
  yield takeEvery(FETCH_SHARED_INTEGRATIONS_SHARED, handleFetchSharedIntegrationsShared);
}

function* watchFetchSharedIntegrations() {
  yield takeEvery(FETCH_SHARED_INTEGRATIONS, handleFetchSharedIntegrations);
}

export function* rootIntegrationsSaga() {
  yield all([
    fork(watchCreateNewIntegration),
    fork(watchFetchIntegrations),
    fork(watchDeleteIntegration),
    fork(watchUpdateIntegration),
    fork(watchFetchOwnedSharedIntegrations),
    fork(watchFetchSharedIntegrationsShared),
    fork(watchFetchSharedIntegrations)
  ]);
}
